import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import Button, { ButtonTypes } from "../../../../../components/Button";
import { InputDate, SelectForm } from "../../../../../components/Input";
import { SegmentedControl } from "../../../../../components/SegmentedControl";
import { DateFormat, formatDate } from "../../../../../utils";
import { ReportFormValues, ReportMode } from "../../../types";
import { REPORT_MODE_OPTIONS } from "../../constants";
import { buildTaxYearOptions } from "../../utils";
import { Actions, Field, FieldLabel, FieldsRow, FormWrapper } from "./styles";
import { ReportFormProps } from "./types";
import {
  isEndOnOrAfterStart,
  isNotInTheFuture,
  isStartOnOrBeforeEnd,
} from "./validation";

export const ReportForm: React.FC<ReportFormProps> = ({
  isGenerating,
  onGenerate,
}) => {
  const [mode, setMode] = useState<ReportMode>("custom");

  const taxYearOptions = useMemo(() => buildTaxYearOptions(), []);
  const today = useMemo(() => new Date(), []);

  const { control, handleSubmit, getValues, trigger, watch } =
    useForm<ReportFormValues>({
      defaultValues: { startDate: null, endDate: null, taxYear: null },
    });

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  // Revalidate the counterpart whenever either date changes, so correcting one field
  // clears a stale error left on the other.
  useEffect(() => {
    if (startDate && endDate) {
      trigger(["startDate", "endDate"]);
    }
  }, [startDate, endDate, trigger]);

  const onSubmit = async (values: ReportFormValues) => {
    if (mode === "taxYear") {
      await onGenerate({ mode, taxYear: Number(values.taxYear?.value) });
      return;
    }

    await onGenerate({
      mode,
      startDate: formatDate(values.startDate, DateFormat.ISO),
      endDate: formatDate(values.endDate, DateFormat.ISO),
    });
  };

  const isCustomRange = mode === "custom";

  return (
    <FormWrapper onSubmit={handleSubmit(onSubmit)} noValidate>
      <SegmentedControl
        options={REPORT_MODE_OPTIONS}
        value={mode}
        onChange={setMode}
        ariaLabel="Report period"
        testId="report-mode-toggle"
      />

      <FieldsRow>
        {isCustomRange ? (
          <>
            <Field>
              <FieldLabel htmlFor="report-start-date">Start date</FieldLabel>
              <InputDate
                id="report-start-date"
                testId="report-start-date"
                name="startDate"
                control={control}
                disabled={isGenerating}
                maxDate={today}
                placeholder="YYYY/MM/DD"
                rules={{
                  required: "Start date is required!",
                  validate: {
                    notInTheFuture: isNotInTheFuture,
                    onOrBeforeEnd: (value: Date | null) =>
                      isStartOnOrBeforeEnd(value, getValues("endDate")),
                  },
                }}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="report-end-date">End date</FieldLabel>
              <InputDate
                id="report-end-date"
                testId="report-end-date"
                name="endDate"
                control={control}
                disabled={isGenerating}
                maxDate={today}
                placeholder="YYYY/MM/DD"
                rules={{
                  required: "End date is required!",
                  validate: {
                    notInTheFuture: isNotInTheFuture,
                    onOrAfterStart: (value: Date | null) =>
                      isEndOnOrAfterStart(value, getValues("startDate")),
                  },
                }}
              />
            </Field>
          </>
        ) : (
          <Field>
            <FieldLabel htmlFor="report-tax-year">Tax year</FieldLabel>
            <SelectForm
              id="report-tax-year"
              testId="report-tax-year"
              name="taxYear"
              control={control}
              defaultValue={null}
              disabled={isGenerating}
              options={taxYearOptions}
              rules={{ required: "Tax year is required!" }}
            />
          </Field>
        )}
      </FieldsRow>

      <Actions>
        <Button
          type={ButtonTypes.primary}
          buttonType="submit"
          disabled={isGenerating}
          isLoading={isGenerating}
          data-testid="generate-report-button"
        >
          Generate
        </Button>
      </Actions>
    </FormWrapper>
  );
};
